import React from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextField,
} from "@mui/material";

interface Question {
  id: number;
  text: string;
  question_type: string;
  possible_answers?: string;
  options?: string;
}

interface Questionnaire {
  id: number;
  name: string;
  questions: Question[];
}

interface ViewQuestionnaireModalProps {
  open: boolean;
  onClose: () => void;
  questionnaire: Questionnaire | null;
}

const ViewQuestionnaireModal: React.FC<ViewQuestionnaireModalProps> = ({
  open,
  onClose,
  questionnaire,
}) => {
  if (!questionnaire) {
    return null;
  }

  const renderQuestion = (question: Question) => {
    switch (question.question_type) {
      case "Yes/No":
        return (
          <FormControl component="fieldset" disabled>
            <RadioGroup row>
              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="No" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        );
      case "Multiple Choice":
        if (question.options) {
          let options: string[];
          try {
            options = JSON.parse(question.options);
          } catch (error) {
            console.error("Error parsing possible_answers:", error);
            options = [];
          }
          return (
            <FormControl component="fieldset" disabled>
              <RadioGroup>
                {options.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={option}
                    control={<Radio />}
                    label={option}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          );
        }
        return null;
      case "Likert Scale":
        return (
          <FormControl component="fieldset" disabled>
            <RadioGroup row>
              {[1, 2, 3, 4, 5].map((value) => (
                <FormControlLabel
                  key={value}
                  value={value.toString()}
                  control={<Radio />}
                  label={value.toString()}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );
      case "Text":
        return <TextField fullWidth disabled placeholder="Text answer" />;
      default:
        return (
          <Typography>
            Unsupported question type: {question.question_type}
          </Typography>
        );
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 600,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          {questionnaire.name}
        </Typography>
        {questionnaire.questions &&
          questionnaire.questions.map((question) => (
            <Box key={question.id} sx={{ mb: 3 }}>
              <FormLabel component="legend">{question.text}</FormLabel>
              {renderQuestion(question)}
            </Box>
          ))}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </Box>
    </Modal>
  );
};

export default ViewQuestionnaireModal;
